import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Grid,
  Divider,
  Button,
  Typography,
  Box,
  TextField,
  Chip
} from '@mui/material';
import {
  Close as CloseIcon,
  Settings as SettingsIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import palette from 'src/theme/palette';
import { toast } from 'react-toastify';
import { respondContact } from 'src/ApiServices';
import useMediaQuery from '@mui/material/useMediaQuery';

const ContactReplyModal = ({ open, onClose, contact, onStatusChange }) => {
  const [customMessage, setCustomMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isMobile = useMediaQuery('(max-width:600px)');

  const handleSubmit = async () => {
    if (!contact) return;

    if (!customMessage.trim()) {
      toast.error('Please enter a response message');
      return;
    }

    try {
      setIsSubmitting(true);

      const payload = {
        response: customMessage,
        id: contact.id
      };

      await respondContact(payload);
      if (onStatusChange) {
        await onStatusChange(contact.id, customMessage);
      }

      toast.success(`Response sent successfully!`);
      setCustomMessage('');
      onClose();
      // window.location.reload();
    } catch (error) {
      console.error('Failed to send response:', error);
      toast.error('Failed to send response');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return 'success';
      case 'rejected':
        return 'error';
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved':
        return <CheckCircleIcon />;
      case 'rejected':
        return <CancelIcon />;
      default:
        return <SettingsIcon />;
    }
  };

  if (!contact) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 2,
          margin: isMobile ? 0 : { xs: 1, sm: 3 },
          boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
          minHeight: isMobile ? '100vh' : '400px',
          maxHeight: isMobile ? '100vh' : '90vh'
        }
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          background: `linear-gradient(135deg, ${palette.primary.main} 0%, ${palette.primary.dark} 100%)`,
          color: palette.primary.contrastText,
          py: 2,
          px: 3,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SettingsIcon />
          <Typography
            sx={{
              fontSize: 18,
              fontFamily: 'poppinsSemiBold'
            }}
          >
            Contact Settings
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{
            color: palette.primary.contrastText,
            '&:hover': {
              backgroundColor: 'rgba(255,255,255,0.1)'
            }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <Grid container spacing={3}>
          {/* Contact Info Summary */}
          <Grid item xs={12}>
            <Box
              sx={{
                backgroundColor: palette.grey[50],
                p: 2,
                borderRadius: 1,
                mt: 2,
                border: `1px solid ${palette.grey[200]}`
              }}
            >
              <Typography variant="subtitle2" sx={{ fontFamily: 'poppinsMedium', mb: 1 }}>
                Contact: {contact.name}
              </Typography>
              <Typography variant="body2" sx={{ color: palette.text.secondary }}>
                {contact.email} • {contact.contact_no}
              </Typography>
              <Box sx={{ mt: 1 }}>
                <Chip
                  icon={getStatusIcon(contact.status || 'pending')}
                  label={contact.status ? contact.status.toUpperCase() : 'PENDING'}
                  color={getStatusColor(contact.status || 'pending')}
                  size="small"
                  variant="outlined"
                />
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>

          {/* Custom Message Section Only */}
          <Grid item xs={12}>
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="h6"
                sx={{
                  fontFamily: 'poppinsSemiBold',
                  color: palette.text.primary,
                  mb: 2
                }}
              >
                Send Response
              </Typography>
            </Box>

            <TextField
              label="Response Message"
              multiline
              rows={6}
              fullWidth
              value={customMessage}
              onChange={(e) => setCustomMessage(e.target.value)}
              placeholder="Type your response message here..."
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': { borderColor: palette.primary.main },
                  '&.Mui-focused fieldset': { borderColor: palette.primary.main }
                }
              }}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 3, gap: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            borderColor: palette.grey[400],
            color: palette.text.primary,
            '&:hover': {
              borderColor: palette.grey[600]
            },
            minWidth: { sm: 100 },
            width: { xs: '100%', sm: 'auto' }
          }}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          startIcon={<CheckCircleIcon />}
          sx={{
            background: `linear-gradient(135deg, ${palette.primary.main} 0%, ${palette.primary.dark} 100%)`,
            color: palette.primary.contrastText,
            '&:hover': {
              background: `linear-gradient(135deg, ${palette.primary.dark} 0%, ${palette.primary.dark} 100%)`
            },
            width: { xs: '100%', sm: 'auto' },
            minWidth: { sm: '140px' }
          }}
          disabled={isSubmitting || !customMessage.trim()}
        >
          {isSubmitting ? 'Sending...' : 'Send Response'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ContactReplyModal;
